import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wand2, Save, Play, Trash2, FileSpreadsheet, FileText, FileDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API } from "@/context/AuthContext";
import { inrShort } from "@/pages/Dashboard";

const EXAMPLES = [
  "AP outstanding grouped by vendor, sorted by highest amount",
  "All AR invoices in the 91-180 bucket with customer, WBS and amount",
  "GSTR-2 ITC eligible invoices above 5 lakh taxable value",
  "WBS-wise sum and count of payables for invoices dated this year",
];
const SOURCE_LABELS = { ap: "AP / Payables", ar: "AR / Receivables", gstr2: "GSTR-2" };
const FORMATS = [
  { fmt: "csv", label: "CSV", icon: FileDown },
  { fmt: "xlsx", label: "Excel", icon: FileSpreadsheet },
  { fmt: "pdf", label: "PDF", icon: FileText },
];

export default function ReportBuilder() {
  const [prompt, setPrompt] = useState("");
  const [result, setResult] = useState(null);
  const [building, setBuilding] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [exporting, setExporting] = useState(null);

  const loadTemplates = () => axios.get(`${API}/report-builder/templates`).then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);

  const build = async (p) => {
    const q = (p || prompt).trim();
    if (!q) return;
    setBuilding(true);
    try {
      const { data } = await axios.post(`${API}/report-builder/generate`, { prompt: q });
      setResult(data);
      setTemplateName(data.spec.title);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Report generation failed");
    } finally {
      setBuilding(false);
    }
  };

  const runTemplate = async (t) => {
    try {
      const { data } = await axios.post(`${API}/report-builder/run`, { spec: t.spec });
      setResult(data);
      setTemplateName(t.name);
      toast.success(`Template "${t.name}" executed`);
    } catch (e) {
      toast.error("Failed to run template");
    }
  };

  const saveTemplate = async () => {
    if (!result) return;
    try {
      await axios.post(`${API}/report-builder/templates`, { name: templateName || result.spec.title, spec: result.spec });
      toast.success("Template saved");
      loadTemplates();
    } catch (e) {
      toast.error("Failed to save template");
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/report-builder/templates/${id}`);
      loadTemplates();
      toast.success("Template deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const exportReport = async (fmt) => {
    if (!result) return;
    setExporting(fmt);
    try {
      const res = await fetch(`${API}/report-builder/export`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ spec: result.spec, format: fmt }),
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${result.spec.title.replace(/\s+/g, "-").toLowerCase()}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} export downloaded`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="report-builder-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">AI Report Builder</h1>
        <p className="text-sm text-slate-500 mt-1">Describe the report you need in plain English — the AI designs the layout, columns, grouping and filters</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm lg:col-span-2 fade-in-up">
          <CardContent className="p-5 space-y-3">
            <Textarea
              data-testid="report-builder-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder='e.g. "Vendor-wise total payables with invoice count, only invoices overdue, sorted by amount"'
              className="min-h-[90px] border-slate-300"
            />
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex flex-wrap gap-2">
                {EXAMPLES.map((ex, i) => (
                  <button key={i} data-testid={`report-example-${i}`} onClick={() => { setPrompt(ex); build(ex); }}
                          className="text-xs border border-slate-200 rounded-full px-3 py-1.5 text-slate-600 hover:bg-sky-50 hover:border-sky-300 transition-colors duration-200">
                    {ex}
                  </button>
                ))}
              </div>
              <Button data-testid="report-builder-generate-button" onClick={() => build()} disabled={building || !prompt.trim()}
                      className="bg-sky-700 hover:bg-sky-800 active:scale-[0.98] transition-transform duration-200">
                <Wand2 className={`h-4 w-4 mr-2 ${building ? "animate-pulse" : ""}`} />
                {building ? "Designing report..." : "Build Report"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm fade-in-up">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800">Saved Templates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-56 overflow-y-auto" data-testid="templates-list">
            {templates.length === 0 && <p className="text-xs text-slate-400">No templates yet. Build a report and save it.</p>}
            {templates.map((t) => (
              <div key={t.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50 transition-colors duration-200">
                <span className="text-sm text-slate-700 flex-1 truncate">{t.name}</span>
                <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500">{SOURCE_LABELS[t.spec.source]}</Badge>
                <button data-testid={`template-run-${t.id}`} onClick={() => runTemplate(t)} className="text-sky-700 hover:text-sky-900" title="Run">
                  <Play className="h-3.5 w-3.5" />
                </button>
                <button data-testid={`template-delete-${t.id}`} onClick={() => deleteTemplate(t.id)} className="text-slate-400 hover:text-red-600" title="Delete">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {result && (
        <Card className="border-slate-200 shadow-sm fade-in-up" data-testid="report-result-card">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="text-lg font-semibold text-slate-800">{result.spec.title}</CardTitle>
                <div className="flex items-center gap-2 mt-1.5">
                  <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">{SOURCE_LABELS[result.spec.source]}</Badge>
                  {result.spec.group_by && <Badge variant="outline" className="border-slate-300 text-slate-500">Grouped by {result.spec.group_by}</Badge>}
                  <span className="text-xs text-slate-400">{result.row_count} rows</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {FORMATS.map(({ fmt, label, icon: Icon }) => (
                  <Button key={fmt} variant="outline" size="sm" data-testid={`custom-export-${fmt}-button`}
                          onClick={() => exportReport(fmt)} disabled={exporting === fmt} className="border-slate-300">
                    <Icon className="h-4 w-4 mr-1.5" />{exporting === fmt ? "..." : label}
                  </Button>
                ))}
                <div className="flex items-center gap-1.5">
                  <Input data-testid="template-name-input" value={templateName} onChange={(e) => setTemplateName(e.target.value)}
                         placeholder="Template name" className="h-9 w-44 border-slate-300 text-sm" />
                  <Button size="sm" data-testid="save-template-button" onClick={saveTemplate} className="bg-slate-900 hover:bg-slate-800">
                    <Save className="h-4 w-4 mr-1.5" />Save
                  </Button>
                </div>
              </div>
            </div>
            {Object.keys(result.totals || {}).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {Object.entries(result.totals).map(([k, v]) => (
                  <span key={k} className="text-xs bg-slate-100 border border-slate-200 rounded-full px-3 py-1 text-slate-600">
                    <span className="font-semibold text-slate-800">{k}:</span> {k === "Count" ? v : inrShort(v)}
                  </span>
                ))}
              </div>
            )}
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              <Table data-testid="report-result-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    {result.headers.map((h) => (
                      <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.rows.slice(0, 100).map((row, i) => (
                    <TableRow key={i} className="border-b border-slate-200 hover:bg-slate-50/50">
                      {row.map((cell, j) => (
                        <TableCell key={j} className="text-sm text-slate-700 whitespace-nowrap">
                          {typeof cell === "number" && cell > 999 ? cell.toLocaleString("en-IN") : String(cell)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {result.rows.length > 100 && <p className="text-xs text-slate-400 mt-2">Showing first 100 of {result.rows.length} rows — exports include all rows</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
